import axios from "axios";
import { AI_LANG_NAMES, STORAGE_KEY, DEFAULT_LANG } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// ---- AI output language ----
// Reads the user's selected UI language from localStorage and maps it to a
// language NAME the backend prompts understand. All AI generations default to
// this so new content follows the chosen language.
export const getAiLanguage = () => {
    try {
        const code = localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
        return AI_LANG_NAMES[code] || AI_LANG_NAMES[DEFAULT_LANG];
    } catch (e) {
        return AI_LANG_NAMES[DEFAULT_LANG];
    }
};

// "auto"/empty -> selected UI language; an explicit choice is respected as-is.
const resolveLang = (language) => {
    if (!language || String(language).toLowerCase() === "auto") return getAiLanguage();
    return language;
};

// ---- AI detail level (Quick / Standard / Expert) ----
const DETAIL_KEY = "learnos_ai_detail";
export const getAiDetail = () => {
    try { return localStorage.getItem(DETAIL_KEY) || "standard"; } catch (e) { return "standard"; }
};
export const setAiDetail = (d) => {
    try { localStorage.setItem(DETAIL_KEY, d); } catch (e) { /* ignore */ }
};

const TOKEN_KEY = "reading_os_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error?.response?.status === 401) {
            clearToken();
            if (!window.location.pathname.match(/\/(login|register)?$/)) {
                // soft redirect to login on auth failure
                if (!["/", "/login", "/register"].includes(window.location.pathname)) {
                    window.location.href = "/login";
                }
            }
        }
        return Promise.reject(error);
    }
);

// ---- Auth ----
export const authApi = {
    register: (data) => api.post("/auth/register", data).then((r) => r.data),
    login: (data) => api.post("/auth/login", data).then((r) => r.data),
    me: () => api.get("/auth/me").then((r) => r.data),
    setOnboarded: () => api.post("/auth/onboarded").then((r) => r.data),
};

// ---- Billing (config-gated Stripe architecture) ----
export const billingApi = {
    plans: () => api.get("/billing/plans").then((r) => r.data),
    config: () => api.get("/billing/config").then((r) => r.data),
    entitlements: () => api.get("/billing/entitlements").then((r) => r.data),
    checkout: (plan, originUrl) =>
        api.post("/billing/checkout", { plan, origin_url: originUrl }).then((r) => r.data),
    checkoutStatus: (sessionId) =>
        api.get(`/billing/checkout/status/${sessionId}`).then((r) => r.data),
    portal: (originUrl) => api.post("/billing/portal", { origin_url: originUrl }).then((r) => r.data),
};

// ---- LearnOS Ambassador Program ----
export const referralsApi = {
    me: () => api.get("/referrals/me").then((r) => r.data),
    validateCode: (code) => api.get(`/referrals/code/${code}`).then((r) => r.data),
};

// ---- Admin ----
export const adminApi = {
    overview: () => api.get("/admin/overview").then((r) => r.data),
    users: (q = "", limit = 50) => api.get("/admin/users", { params: { q, limit } }).then((r) => r.data),
    updateUser: (id, data) => api.patch(`/admin/users/${id}`, data).then((r) => r.data),
    referrals: (status = "") => api.get("/admin/referrals", { params: status ? { status } : {} }).then((r) => r.data),
    updateReferral: (id, data) => api.patch(`/admin/referrals/${id}`, data).then((r) => r.data),
    payouts: (status = "") => api.get("/admin/payouts", { params: status ? { status } : {} }).then((r) => r.data),
    createPayout: (data) => api.post("/admin/payouts", data).then((r) => r.data),
    updatePayout: (id, data) => api.patch(`/admin/payouts/${id}`, data).then((r) => r.data),
};

// ---- Documents ----
export const docsApi = {
    list: () => api.get("/documents").then((r) => r.data),
    get: (id) => api.get(`/documents/${id}`).then((r) => r.data),
    uploadFile: (file, onProgress, workspaceId) => {
        const form = new FormData();
        form.append("file", file);
        if (workspaceId) form.append("workspace_id", workspaceId);
        return api
            .post("/documents/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: onProgress,
            })
            .then((r) => r.data);
    },
    uploadUrl: (url, language = "auto", workspaceId) =>
        api.post("/documents/url", { url, language, workspace_id: workspaceId }).then((r) => r.data),
    uploadText: (title, text, language = "auto", workspaceId) =>
        api.post("/documents/text", { title, text, language, workspace_id: workspaceId }).then((r) => r.data),
    remove: (id) => api.delete(`/documents/${id}`).then((r) => r.data),
    retry: (id) => api.post(`/documents/${id}/retry`).then((r) => r.data),
    updateProgress: (id, progress) =>
        api.put(`/documents/${id}/progress`, progress).then((r) => r.data),
};

// ---- AI ----
export const aiApi = {
    summary: (id, type, language = "auto", refresh = false, detail = "standard") =>
        api.post(`/documents/${id}/summary`, { type, language: resolveLang(language), refresh, detail }).then((r) => r.data),
    listSummaries: (id) => api.get(`/documents/${id}/summaries`).then((r) => r.data),
    extraction: (id, language = "auto", refresh = false) =>
        api.post(`/documents/${id}/extraction`, { language: resolveLang(language), refresh }).then((r) => r.data),
    highlights: (id, language = "auto", refresh = false) =>
        api.post(`/documents/${id}/highlights`, { language: resolveLang(language), refresh }).then((r) => r.data),
    chatHistory: (id) => api.get(`/documents/${id}/chat`).then((r) => r.data),
    chat: (id, message, language = "auto", detail = "standard") =>
        api.post(`/documents/${id}/chat`, { message, language: resolveLang(language), detail }).then((r) => r.data),
    // Streaming chat (SSE): keeps the connection alive for long "professor" answers
    // and streams tokens live. Calls onDelta(text) as tokens arrive, then
    // onDone({sources, message_id}). Returns an AbortController.
    chatStream: (id, message, { language = "auto", detail = "standard", onDelta, onDone, onError } = {}) => {
        const controller = new AbortController();
        (async () => {
            try {
                const res = await fetch(`${API}/documents/${id}/chat/stream`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify({ message, language: resolveLang(language), detail }),
                    signal: controller.signal,
                });
                if (!res.ok || !res.body) throw new Error(`stream_failed_${res.status}`);
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buf = "";
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buf += decoder.decode(value, { stream: true });
                    const parts = buf.split("\n\n");
                    buf = parts.pop() || "";
                    for (const p of parts) {
                        const line = p.trim();
                        if (!line.startsWith("data:")) continue;
                        let evt;
                        try { evt = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
                        if (evt.type === "delta") onDelta?.(evt.content);
                        else if (evt.type === "done") onDone?.(evt);
                        else if (evt.type === "error") onError?.(new Error(evt.detail || "stream error"));
                    }
                }
            } catch (e) {
                if (e.name !== "AbortError") onError?.(e);
            }
        })();
        return controller;
    },
    clearChat: (id) => api.delete(`/documents/${id}/chat`).then((r) => r.data),
    explain: (id, term, context = "", language = "auto", refresh = false, detail = "standard") =>
        api.post(`/documents/${id}/explain`, { term, context, language: resolveLang(language), refresh, detail }).then((r) => r.data),
};

// ---- Saved terms + Glossary + Adaptive Learning Profile (Click-to-Explain) ----
export const termsApi = {
    save: ({ document_id, term, definition, context, word_start }) =>
        api.post("/terms/save", { document_id, term, definition, context, word_start }).then((r) => r.data),
    listSaved: () => api.get("/terms/saved").then((r) => r.data),
    note: ({ document_id, term, note, context, word_start }) =>
        api.post("/terms/note", { document_id, term, note, context, word_start }).then((r) => r.data),
    profile: () => api.get("/terms/profile").then((r) => r.data),
    // My Glossary
    glossary: (language = "") => api.get("/glossary", { params: language ? { language } : {} }).then((r) => r.data),
    updateItem: (id, data) => api.patch(`/glossary/${id}`, data).then((r) => r.data),
    removeItem: (id) => api.delete(`/glossary/${id}`).then((r) => r.data),
};

// ---- Analytics ----
export const analyticsApi = {
    event: (payload) => api.post("/analytics/event", payload).then((r) => r.data),
    overview: () => api.get("/analytics/overview").then((r) => r.data),
};

// ---- Audio (Phase 4 prep) ----
export const audioApi = {
    capabilities: () => api.get("/audio/capabilities").then((r) => r.data),
};

// ---- Flashcards + Spaced Repetition ----
export const flashcardsApi = {
    generate: (docId, opts = {}) => api.post(`/documents/${docId}/flashcards/generate`, { ...opts, language: resolveLang(opts.language) }).then((r) => r.data),
    add: (docId, card) => api.post(`/documents/${docId}/flashcards/add`, card).then((r) => r.data),
    list: (docId) => api.get(`/documents/${docId}/flashcards`).then((r) => r.data),
    due: (documentId) => api.get("/flashcards/due", { params: documentId ? { document_id: documentId } : {} }).then((r) => r.data),
    review: (cardId, rating) => api.post(`/flashcards/${cardId}/review`, { rating }).then((r) => r.data),
    update: (cardId, data) => api.put(`/flashcards/${cardId}`, data).then((r) => r.data),
    remove: (cardId) => api.delete(`/flashcards/${cardId}`).then((r) => r.data),
    regenerate: (cardId, mode = "improve") => api.post(`/flashcards/${cardId}/regenerate`, { mode }).then((r) => r.data),
    fromTopics: (docId, opts = {}) => api.post(`/documents/${docId}/flashcards/from-topics`, { ...opts, language: resolveLang(opts.language) }).then((r) => r.data),
    // Chat Quick Action: distil a chat exchange into ONE proposed card (preview, not saved).
    fromChat: (docId, { question, answer, language = "auto" } = {}) =>
        api.post(`/documents/${docId}/flashcards/from-chat`, { question, answer, language: resolveLang(language) }).then((r) => r.data),
};

// ---- Quiz + Exam ----
export const quizApi = {
    generate: (docId, opts = {}) => api.post(`/documents/${docId}/quiz/generate`, { ...opts, language: resolveLang(opts.language) }).then((r) => r.data),
    list: (docId) => api.get(`/documents/${docId}/quizzes`).then((r) => r.data),
    get: (quizId) => api.get(`/quizzes/${quizId}`).then((r) => r.data),
    submit: (quizId, answers, durationSec = 0) => api.post(`/quizzes/${quizId}/submit`, { answers, duration_sec: durationSec }).then((r) => r.data),
};

// ---- Learning engine ----
export const learningApi = {
    docScores: (docId) => api.get(`/documents/${docId}/scores`).then((r) => r.data),
    overview: () => api.get("/learning/overview").then((r) => r.data),
    coach: (refresh = false, detail = "standard") => api.get("/learning/coach", { params: { refresh, language: getAiLanguage(), detail } }).then((r) => r.data),
    reflection: (docId, refresh = false) => api.post(`/documents/${docId}/reflection`, { refresh, language: getAiLanguage() }).then((r) => r.data),
};

// ---- Workspaces (Phase 4 — the "Personal AI Professor" knowledge base) ----
export const workspacesApi = {
    list: () => api.get("/workspaces").then((r) => r.data),
    get: (id) => api.get(`/workspaces/${id}`).then((r) => r.data),
    create: (data) => api.post("/workspaces", data).then((r) => r.data),
    update: (id, data) => api.patch(`/workspaces/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/workspaces/${id}`).then((r) => r.data),
    addSource: (id, docId) => api.post(`/workspaces/${id}/documents/${docId}`).then((r) => r.data),
    scores: (id) => api.get(`/workspaces/${id}/scores`).then((r) => r.data),
    // chat
    chatHistory: (id) => api.get(`/workspaces/${id}/chat`).then((r) => r.data),
    clearChat: (id) => api.delete(`/workspaces/${id}/chat`).then((r) => r.data),
    chatStream: (id, message, { language = "auto", detail = "standard", onDelta, onDone, onError } = {}) => {
        const controller = new AbortController();
        (async () => {
            try {
                const res = await fetch(`${API}/workspaces/${id}/chat/stream`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify({ message, language: resolveLang(language), detail }),
                    signal: controller.signal,
                });
                if (!res.ok || !res.body) throw new Error(`stream_failed_${res.status}`);
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buf = "";
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buf += decoder.decode(value, { stream: true });
                    const parts = buf.split("\n\n");
                    buf = parts.pop() || "";
                    for (const p of parts) {
                        const line = p.trim();
                        if (!line.startsWith("data:")) continue;
                        let evt;
                        try { evt = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
                        if (evt.type === "delta") onDelta?.(evt.content);
                        else if (evt.type === "done") onDone?.(evt);
                        else if (evt.type === "error") onError?.(new Error(evt.detail || "stream error"));
                    }
                }
            } catch (e) {
                if (e.name !== "AbortError") onError?.(e);
            }
        })();
        return controller;
    },
    // summaries / knowledge
    summary: (id, type, language = "auto", refresh = false, detail = "standard") =>
        api.post(`/workspaces/${id}/summary`, { type, language: resolveLang(language), refresh, detail }).then((r) => r.data),
    listSummaries: (id) => api.get(`/workspaces/${id}/summaries`).then((r) => r.data),
    knowledge: (id, language = "auto", refresh = false) =>
        api.post(`/workspaces/${id}/knowledge`, { language: resolveLang(language), refresh }).then((r) => r.data),
    // cross-document intelligence (P5)
    getInsights: (id, language = "") => api.get(`/workspaces/${id}/insights`, { params: language ? { language } : {} }).then((r) => r.data),
    insights: (id, language = "auto", refresh = false) =>
        api.post(`/workspaces/${id}/insights`, { language: resolveLang(language), refresh }).then((r) => r.data),
    // learning
    flashcardsList: (id) => api.get(`/workspaces/${id}/flashcards`).then((r) => r.data),
    flashcardsGenerate: (id, opts = {}) => api.post(`/workspaces/${id}/flashcards/generate`, { ...opts, language: resolveLang(opts.language) }).then((r) => r.data),
    flashcardAdd: (id, card) => api.post(`/workspaces/${id}/flashcards/add`, card).then((r) => r.data),
    flashcardFromChat: (id, { question, answer, language = "auto" } = {}) =>
        api.post(`/workspaces/${id}/flashcards/from-chat`, { question, answer, language: resolveLang(language) }).then((r) => r.data),
    quizGenerate: (id, opts = {}) => api.post(`/workspaces/${id}/quiz/generate`, { ...opts, language: resolveLang(opts.language) }).then((r) => r.data),
    quizList: (id) => api.get(`/workspaces/${id}/quizzes`).then((r) => r.data),
};
